import { useEffect, useRef } from 'react';

import useActions from '@app/store/hooks/useActions';
import { useTypedDispatch } from '@app/store/hooks/useTypedDispatch';

import {
  routingConfigVersionApi,
  useCreateRoutingConfigDraftMutation,
  usePatchRoutingConfigVersionMetadataMutation,
  useUpdateRoutingConfigVersionMutation,
} from '../api';
import { parseConfigPathErrors } from '../lib/configErrors';
import { toPutPayload } from '../lib/editorMappers';
import { IConfigIssue, IRoutingConfigVersion } from '../model/types';
import { useEditorSession } from '../slice';

export interface ISaveDraftInput {
  name: string;
  // Empty means "no comment" — sent as null, since the backend reads an empty
  // string as "keep the current one".
  comment: string;
}

export type TSaveDraftResult =
  | { status: 'saved'; version: IRoutingConfigVersion }
  | { status: 'failed'; stage: 'create' | 'metadata' | 'update'; issues: IConfigIssue[] };

interface IUseSaveDraftOptions {
  // Fires once the draft exists and its metadata is settled (before the PUT), so the
  // caller pins the URL to it and a failed PUT retries there, never creating a second draft.
  onDraftCreated?: (draft: IRoutingConfigVersion) => void;
}

// Save Draft PUTs the whole working copy. A draft base takes the PUT directly; a
// published/archived base needs a draft created first (name required), then re-keyed.
// Name and comment ride along on a PATCH placed before the PUT, so the PUT's single
// invalidation covers the whole save.
export const useSaveDraft = ({ onDraftCreated }: IUseSaveDraftOptions = {}) => {
  const session = useEditorSession();
  // The save modal keeps the saveDraft it was opened with. A retry after the draft was
  // created must see the moved session, not the snapshot from before — hence the ref,
  // written after commit so a discarded render can never leak into it. saveDraft only
  // runs from a click, long after the effect has caught up.
  const sessionRef = useRef(session);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const dispatch = useTypedDispatch();
  const { moveSessionToDraft, syncSessionBase } = useActions();
  const [createDraft, { isLoading: isCreating }] = useCreateRoutingConfigDraftMutation();
  const [patchMetadata, { isLoading: isPatching }] = usePatchRoutingConfigVersionMetadataMutation();
  const [updateVersion, { isLoading: isUpdating }] = useUpdateRoutingConfigVersionMutation();

  // A published/archived base is cloned into a new draft; a draft base is saved in place.
  const isCreatingDraft = session !== null && session.savedDraftId === null;

  const saveDraft = async ({ name, comment }: ISaveDraftInput): Promise<TSaveDraftResult> => {
    const current = sessionRef.current;
    if (!current) {
      return {
        status: 'failed',
        stage: 'update',
        issues: [{ path: '', message: 'There is no edit session to save.' }],
      };
    }

    const isNewDraft = current.savedDraftId === null;
    let targetId = current.savedDraftId;
    let created: IRoutingConfigVersion | null = null;
    const trimmedName = name.trim();
    const trimmedComment = comment.trim();

    if (isNewDraft) {
      try {
        const response = await createDraft({
          name: trimmedName,
          source_version_id: current.versionId,
        }).unwrap();
        created = response.data;
        targetId = created.id;
        moveSessionToDraft(created);
      } catch (error) {
        return { status: 'failed', stage: 'create', issues: parseConfigPathErrors(error) };
      }
    }

    // The draft POST takes the name but ignores the comment, so a fresh draft only
    // patches a comment it actually has; an existing draft patches both fields.
    if (!isNewDraft || trimmedComment) {
      try {
        const updated = await patchMetadata({
          id: targetId,
          body: {
            ...(isNewDraft ? {} : { name: trimmedName }),
            comment: trimmedComment || null,
          },
        }).unwrap();
        // The PATCH bumped updated_at. Refresh the session's guard first, then the cached
        // version: the hydrate check keeps the session, and the card and the next modal
        // already show the saved metadata even if the PUT fails next.
        syncSessionBase(updated.data);
        dispatch(
          routingConfigVersionApi.util.upsertQueryData('getRoutingConfigVersion', targetId, updated),
        );
      } catch (error) {
        // A draft created just above survives (and gets the URL), so the retry saves
        // into it rather than cloning the base a second time.
        if (created) onDraftCreated?.(created);
        return { status: 'failed', stage: 'metadata', issues: parseConfigPathErrors(error) };
      }
    }

    // Pinning the URL earlier would let the new draft's GET race the PATCH and trip the
    // updated_at guard; after the metadata step it reads back what the session expects.
    if (created) onDraftCreated?.(created);

    try {
      const updated = await updateVersion({
        id: targetId,
        body: toPutPayload(current.draft),
      }).unwrap();

      return { status: 'saved', version: updated.data };
    } catch (error) {
      return { status: 'failed', stage: 'update', issues: parseConfigPathErrors(error) };
    }
  };

  return { saveDraft, isCreatingDraft, isSaving: isCreating || isPatching || isUpdating };
};
